import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Grid,
  Button,
  CssBaseline,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Menu,
  MenuItem,
} from "@mui/material";
import { Close, Add, ExpandMore as ExpandMoreIcon } from "@mui/icons-material";

import { callApi } from "../../../utils/apiClient";
import { Production_API_ENDPOINTS } from "../../../config/api/apiCallProduction";
import AddUpdateWasteLocation from "./AddUpdateWasteLocation";
import CallAlertBar from "../../../Layout/Alertbar";

const WasteLocationMasterIndex = ({ handleBackWinderMachineEntry }) => {
  const [list, setList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [filters, setFilters] = useState({
    location_name: "",
    machine_name: "",
    status: "",
  });

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuText, setMenuText] = useState("All Waste Locations");

  // Alerts
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("info");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("");

  // ===========================
  // Menu Handlers
  // ===========================
  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleMenuItemClick = (option) => {
    setMenuText(option);
    handleMenuClose();

    if (option === "All Waste Locations") setFilteredList(list);
    else if (option === "Active Locations")
      setFilteredList(list.filter((x) => x.status === true || x.status === "Active"));
    else if (option === "Inactive Locations")
      setFilteredList(list.filter((x) => x.status === false || x.status === "Inactive"));
  };

  // ===========================
  // Load List
  // ===========================
  const loadWasteLocationList = () => {
    const payload = {
      p_company_id: localStorage.getItem("companyid"),
      p_plant_id: 1,
    };

    callApi(
      Production_API_ENDPOINTS.repulpmaster?.wastelocationlist || "/api/repulpmaster/wastelocationlist",
      "POST",
      { "Content-Type": "application/json" },
      payload
    )
      .then((res) => {
        setList(res || []);
        setFilteredList(res || []);
      })
      .catch(() => {
        setList([]);
        setFilteredList([]);
        setAlertSeverity("error");
        setAlertTitle("Error");
        setAlertMessage("Failed to load waste location list.");
        setAlertOpen(true);
      });
  };

  useEffect(() => {
    loadWasteLocationList();
  }, []);

  // ===========================
  // Column Filters
  // ===========================
  useEffect(() => {
    const filtered = list.filter((row) => {
      const statusText =
        row.status === true || row.status === "Active" ? "active" : "inactive";

      return (
        (row.location_name || "")
          .toLowerCase()
          .includes(filters.location_name.toLowerCase()) &&
        (row.machine_name || "")
          .toLowerCase()
          .includes(filters.machine_name.toLowerCase()) &&
        statusText.includes(filters.status.toLowerCase())
      );
    });

    setFilteredList(filtered);
  }, [filters, list]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // ===========================
  // Form Handlers
  // ===========================
  const handleAdd = () => {
    setFormData({});
    setIsFormOpen(true);
  };

  const handleEdit = (row) => {
    setFormData(row);
    setIsFormOpen(true);
  };

  const handleExit = () => {
    handleBackWinderMachineEntry();
  };

  return (
    <Grid>
      {!isFormOpen && (
        <>
          <CssBaseline />
          <AppBar
            position="static"
            sx={{
              backgroundColor: "inherit",
              borderRadius: "0px",
              height: "48px",
              justifyContent: "center",
            }}
          >
            <Toolbar>
              {/* 🔽 Menu for All/Active/Inactive */}
              <Box>
                <Button
                  color="secondary"
                  onClick={handleMenuClick}
                  endIcon={<ExpandMoreIcon sx={{ color: "teal", fontSize: 18 }} />}
                  sx={{ textTransform: "none", fontSize: 16, fontWeight: 500 }}
                >
                  {menuText}
                </Button>

                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                  <MenuItem onClick={() => handleMenuItemClick("All Waste Locations")}>
                    All Waste Locations
                  </MenuItem>
                  <MenuItem onClick={() => handleMenuItemClick("Active Locations")}>
                    Active Locations
                  </MenuItem>
                  <MenuItem onClick={() => handleMenuItemClick("Inactive Locations")}>
                    Inactive Locations
                  </MenuItem>
                </Menu>
              </Box>

              <Button
                variant="contained"
                color="secondary"
                startIcon={<Add />}
                sx={{ ml: "auto" }}
                onClick={handleAdd}
              >
                New Location
              </Button>

              <IconButton onClick={handleExit}>
                <Close />
              </IconButton>
            </Toolbar>
          </AppBar>

          {/* Alerts */}
          <CallAlertBar
            parentRollMsgOpen={alertOpen}
            setParentRollMsgOpen={setAlertOpen}
            parentRollSeverityvalue={alertSeverity}
            setParentRollSeverityvalue={setAlertSeverity}
            parentRollAlertTitleValue={alertTitle}
            setParentRollAlertTitleValue={setAlertTitle}
            parentRollMessageValue={alertMessage}
            setParentRollMessageValue={setAlertMessage}
          />

          {/* 🔽 Table */}
          <Box p={1}>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                     <TableCell>ID</TableCell>
                    <TableCell>Waste Location</TableCell>
                    <TableCell>Machine</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Search Location"
                        value={filters.location_name}
                        onChange={(e) => handleFilterChange("location_name", e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Search Machine"
                        value={filters.machine_name}
                        onChange={(e) => handleFilterChange("machine_name", e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Search Status"
                        value={filters.status}
                        onChange={(e) => handleFilterChange("status", e.target.value)}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredList.map((row) => (
                    <TableRow key={row.location_id}>
                      <TableCell
                        sx={{ color: "teal", cursor: "pointer" }}
                        onClick={() => handleEdit(row)}
                      >
                        {row.location_id}
                      </TableCell>
                      <TableCell>{row.location_name}</TableCell>
                      <TableCell>{row.machine_name}</TableCell>
                      <TableCell>{row.status ? "Active" : "Inactive"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      )}

      {isFormOpen && (
        <AddUpdateWasteLocation
          formData={formData}
          setFormData={setFormData}
          setIsFormOpen={setIsFormOpen}
          loadWasteLocationList={loadWasteLocationList}
        />
      )}
    </Grid>
  );
};

export default WasteLocationMasterIndex;
